import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import { CircleAlert, CircleCheck, Plus, Trash2 } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { FormSection } from '@components/form-section';
import { ImageUpload } from '@components/image-upload';
import { PageHeader } from '@components/page-header';
import { Select } from '@components/select';
import { Spinner } from '@components/spinner';
import { StickyBar } from '@components/sticky-bar';
import { FieldError, FieldLabel, TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { cn } from '@core/utils/cn';
import { formatEnumLabel } from '@core/utils/format-enum-label';
import {
    TeamAccess,
    TeamLeaderboardVisibility,
    TeamMemberVisibility
} from '@api/ChallengeClient';

import {
    MAX_TEAMS_PER_SUBMISSION,
    Row,
    useBulkCreateTeamsStore
} from './bulk-create-teams-store';

const enumOptions = <T extends string>(values: T[]) =>
    values.map((value) => ({ label: formatEnumLabel(value), value }));

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p className="text-xs text-muted-foreground">{children}</p>
);

interface NameRowProps {
    row: Row;
    index: number;
    isLast: boolean;
    canRemove: boolean;
    submitting: boolean;
    inputRef?: React.RefObject<HTMLInputElement>;
    onChangeName: (id: string, name: string) => void;
    onRemove: (id: string) => void;
    onEnter: () => void;
}

// Not memoized: callbacks are recreated every parent render (the store
// returns fresh closures), so `React.memo` would always invalidate. Network
// dominates total time; the few extra reconciliations are negligible.
const NameRow: React.FC<NameRowProps> = ({
    row,
    index,
    isLast,
    canRemove,
    submitting,
    inputRef,
    onChangeName,
    onRemove,
    onEnter
}) => {
    const isLocked = row.status === 'submitting' || row.status === 'success';
    const showSpinner = row.status === 'submitting';
    const showSuccess = row.status === 'success';
    const showError = row.status === 'error';

    return (
        <div className="flex items-start gap-2">
            <div className="flex h-9 w-8 shrink-0 items-center justify-center self-end">
                {showSpinner && <Spinner size="sm" aria-label="Submitting" />}
                {showSuccess && (
                    <CircleCheck
                        className="size-5 text-success"
                        aria-label="Created"
                    />
                )}
                {showError && (
                    <CircleAlert
                        className="size-5 text-destructive"
                        aria-label={`Failed: ${row.errorMessage}`}
                    />
                )}
            </div>
            <div className="flex w-full flex-col gap-1.5">
                <FieldLabel label={`Team name ${index + 1}`} />
                <Input
                    ref={inputRef}
                    value={row.name}
                    maxLength={60}
                    onChange={(e) => onChangeName(row.id, e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            // Only the last row appends a new one on Enter.
                            if (isLast) onEnter();
                        }
                    }}
                    disabled={isLocked}
                    aria-invalid={showError}
                />
                <FieldError error={showError ? row.errorMessage : undefined} />
            </div>
            <Button
                variant="ghost"
                size="icon"
                aria-label="Remove team"
                className="self-end"
                onClick={() => onRemove(row.id)}
                disabled={!canRemove || isLocked || submitting}
            >
                <Trash2 />
            </Button>
        </div>
    );
};

const BulkCreateTeamsForm = observer(
    ({ communityId }: { communityId: number }) => {
        const {
            fields,
            rows,
            addRow,
            removeRow,
            setRowName,
            onSubmit,
            onCancel,
            submitting,
            progress,
            imageUpload
        } = useBulkCreateTeamsStore(communityId);

        const lastInputRef = useRef<HTMLInputElement>(null);

        const handleAddRow = () => {
            addRow();
            // Focus the new (last) input after it renders.
            setTimeout(() => lastInputRef.current?.focus(), 0);
        };

        const submitDisabled = submitting ? false : imageUpload.loading;

        return (
            <>
                <PageHeader title="Create Multiple Teams" />

                <div className="flex flex-col gap-6 pb-24">
                    <FormSection
                        title="Shared fields"
                        description="These values are applied to every team created."
                    >
                        <div className="grid gap-4 md:grid-cols-3">
                            <div className="md:col-span-3">
                                <TextField
                                    label="Description"
                                    type="text"
                                    value={fields.description.value}
                                    onChange={(value) =>
                                        fields.description.onChange(
                                            String(value)
                                        )
                                    }
                                    error={fields.description.error}
                                    required
                                    multiline
                                    disabled={submitting}
                                />
                            </div>

                            <div
                                className={cn(
                                    'md:col-span-3',
                                    submitting &&
                                        'pointer-events-none opacity-60'
                                )}
                            >
                                <ImageUpload
                                    onUpload={imageUpload.handleUpload}
                                    loading={imageUpload.loading}
                                    error={imageUpload.error}
                                    previewUrl={imageUpload.previewUrl}
                                    label="Team Image"
                                    helperText="Drag and drop a team image here, or click to select"
                                />
                            </div>
                            {(fields.image.bucketName.error ||
                                fields.image.objectKey.error) && (
                                <Alert
                                    variant="destructive"
                                    className="md:col-span-3"
                                >
                                    <AlertDescription>
                                        {fields.image.bucketName.error ||
                                            fields.image.objectKey.error}
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="flex flex-col gap-1.5 md:col-span-2">
                                <TextField
                                    label="Manager User ID"
                                    type="text"
                                    value={fields.managerUserId.value}
                                    onChange={(value) =>
                                        fields.managerUserId.onChange(
                                            String(value)
                                        )
                                    }
                                    error={fields.managerUserId.error}
                                    required
                                    disabled={submitting}
                                />
                                {!fields.managerUserId.error && (
                                    <Hint>
                                        Paste the user ID. The selected user
                                        will be added as a team member of every
                                        team.
                                    </Hint>
                                )}
                            </div>

                            <Select
                                label="Access"
                                required
                                value={fields.access.value}
                                disabled={submitting}
                                options={enumOptions(Object.values(TeamAccess))}
                                onChange={(value: TeamAccess) =>
                                    fields.access.onChange(value)
                                }
                            />
                            <Select
                                label="Member Visibility"
                                required
                                value={fields.memberVisibility.value}
                                disabled={submitting}
                                options={enumOptions(
                                    Object.values(TeamMemberVisibility)
                                )}
                                onChange={(value: TeamMemberVisibility) =>
                                    fields.memberVisibility.onChange(value)
                                }
                            />
                            <Select
                                label="Leaderboard Visibility"
                                required
                                value={fields.leaderboardVisibility.value}
                                disabled={submitting}
                                options={enumOptions(
                                    Object.values(TeamLeaderboardVisibility)
                                )}
                                onChange={(value: TeamLeaderboardVisibility) =>
                                    fields.leaderboardVisibility.onChange(value)
                                }
                            />
                            <div className="flex flex-col gap-1.5">
                                <TextField
                                    label="Member Limit"
                                    type="number"
                                    value={fields.memberLimitInput.value}
                                    onChange={(value) =>
                                        fields.memberLimitInput.onChange(
                                            String(value)
                                        )
                                    }
                                    error={fields.memberLimitInput.error}
                                    disabled={submitting}
                                />
                                {!fields.memberLimitInput.error && (
                                    <Hint>Leave empty for no limit</Hint>
                                )}
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        title="Team names"
                        description="One team will be created per name."
                    >
                        <div className="flex flex-col gap-3">
                            {rows.map((row, index) => (
                                <NameRow
                                    key={row.id}
                                    row={row}
                                    index={index}
                                    isLast={index === rows.length - 1}
                                    canRemove={rows.length > 1}
                                    submitting={submitting}
                                    inputRef={
                                        index === rows.length - 1
                                            ? lastInputRef
                                            : undefined
                                    }
                                    onChangeName={setRowName}
                                    onRemove={removeRow}
                                    onEnter={handleAddRow}
                                />
                            ))}
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                            <Button
                                variant="ghost"
                                onClick={handleAddRow}
                                disabled={
                                    submitting ||
                                    rows.length >= MAX_TEAMS_PER_SUBMISSION
                                }
                            >
                                <Plus />
                                Add another team
                            </Button>
                            {rows.length >= MAX_TEAMS_PER_SUBMISSION && (
                                <Hint>
                                    Maximum {MAX_TEAMS_PER_SUBMISSION} teams per
                                    submission
                                </Hint>
                            )}
                        </div>
                    </FormSection>
                </div>

                <StickyBar>
                    <Button variant="ghost" onClick={onCancel}>
                        {submitting ? 'Stop' : 'Cancel'}
                    </Button>
                    <Button onClick={onSubmit} disabled={submitDisabled}>
                        {submitting && progress
                            ? `Creating teams… (${progress.current}/${progress.total})`
                            : 'Create Teams'}
                    </Button>
                    {progress && (
                        <div
                            role="status"
                            aria-live="polite"
                            className="sr-only"
                        >
                            Creating team {progress.current} of {progress.total}
                        </div>
                    )}
                </StickyBar>
            </>
        );
    }
);

const InvalidCommunity = ({ message }: { message: string }) => (
    <Alert variant="destructive">
        <AlertDescription>{message}</AlertDescription>
    </Alert>
);

export const BulkCreateTeams: React.FC = () => {
    const { communityId } = useParams();

    if (!communityId) {
        return <InvalidCommunity message="Missing community identifier." />;
    }

    const parsed = Number(communityId);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        return <InvalidCommunity message="Invalid community identifier." />;
    }

    return <BulkCreateTeamsForm communityId={parsed} />;
};
